import { Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, Signal, computed, signal } from "@preact/signals";
import { store } from "~/core/store";
import { ApiSliceParams } from "../interfaces";
import { createSubscribe } from "../utils";
import { CreateTaskAssignmentRequest, TaskAssignment } from "./interfaces";

export const makeAssignment = ({ connection }: ApiSliceParams) => {
    const assignments = signal<TaskAssignment[]>([]);
    const taskStore: Record<string, ReadonlySignal<TaskAssignment[]>> = {};
    const loadingStore: Record<string, Signal<boolean>> = {};
    const loading = signal(false);

    const loadAssignment = async (
        id: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(id) ? id : [id];
        const items = con.task.assignment.get({ id: finalId });
        const result: TaskAssignment[] = [];

        for await (const item of items) {
            const finalAssignment = {
                ...item,
                id: item.id.toString(),
                _id: item.id,
            };

            assignments.value = [...assignments.peek(), finalAssignment];
            result.push(finalAssignment);
        }

        const { task: taskApi } = store.api;

        for (const assignment of result) {
            taskApi.loadTaskUser(assignment.task_user);
        }

        return result;
    };

    const loadTask = async (taskId: Readonly<Ulid>) => {
        const con = connection.value;
        const loading = loadingStore[taskId.toString()];

        if (loading.peek()) {
            return;
        }
        try {
            loading.value = true;
            const list = con.task.assignment.list({
                task: taskId,
            });

            for await (const id of list) {
                await loadAssignment(id);
            }
        } catch (error) {
            console.error(error);
        } finally {
            loading.value = false;
        }
    };

    const load = async (taskId: Readonly<Ulid>[]) => {
        loading.value = true;

        for (const id of taskId) {
            if (!checkStore(id)) {
                await loadTask(id);
            }
        }

        loading.value = false;
    };

    const create = async (request: CreateTaskAssignmentRequest) => {
        const con = connection.value;

        if (!con) {
            throw new Error("Connection is not available or task not found");
        }

        try {
            await con.task.assignment.create({
                assignee: request.assignee,
                task: request.task,
                start: request.start,
                daymax: request.daymax,
                planned: request.planned,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const checkStore = (taskId: Readonly<Ulid>) => {
        const key = taskId.toString();

        if (!(key in taskStore)) {
            loadingStore[key] = signal(false);
            taskStore[key] = computed(() =>
                assignments.value.filter((item) => item.task.equals(taskId)),
            );

            return false;
        }

        return true;
    };

    const task = (taskId: Readonly<Ulid>) => {
        if (!checkStore(taskId)) {
            loadTask(taskId);
        }

        return taskStore[taskId.toString()];
    };

    const taskLoading = (taskId: Readonly<Ulid>) => {
        if (!checkStore(taskId)) {
            loadTask(taskId);
        }

        return loadingStore[taskId.toString()];
    };

    const subscribe = createSubscribe(
        assignments,
        connection,
        (con) => con.task.assignment.subscribe(),
        loadAssignment,
    );

    return {
        assignments,
        loading,
        load,
        create,
        task,
        taskLoading,
        subscribe,
    };
};
